import { useEffect, useMemo, useState } from "react";
import UpandDownArrow from "../../../assets/svg/dateIcon.svg";
import { useTable, usePagination, useSortBy } from "react-table";
import { useSticky } from "react-table-sticky";
import InputField from "../../../components/InputField";
import SearchIcon from "../../../assets/SearchIcon";
import { Link, useNavigate } from "react-router-dom";
import Button from "../../../components/Button";
import ManagementTableLayout from "../../../components/MangementTable";
import PlusIcon from "../../../assets/PlusIcon";
import Pagination from "../../../components/Pagination";
import EditIcon from "../../../assets/EditIcon";
import { getProject } from "../../../api/project";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { stateAtom } from "../../../atom";
import { checkPermissions, formatDate } from "../../../utils/helper";
import useDebounce from "../../../hooks/useDebounce";
import UpArrow2 from "../../../assets/svg-tsx/UpArrow2";
import { Tooltip } from "antd";
import SelectField2 from "../../../components/SelectedField2";
import { PROJECT_STATUS } from "../../../utils/data";
import FilterIcon from "../../../assets/svg-tsx/FilterIcon";

const ProjectTable = ({ setProjectCount }: any) => {
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageLength, setPageLength] = useState<number>(25);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [inputVal, setInputVal] = useState("");
  const navigate = useNavigate();
  const [settableData, setSettableData] = useState<any>([]);
  const [isTogglingStatus, setIsTogglingStatus] = useState("");
  const [startIndex, setStartIndex] = useState(0);

  const [filteredProjectStatus, setFilteredProjectStatus] = useState("");

  const debouncedInputVal = useDebounce(inputVal, 500);

  const stateList = useRecoilValue(stateAtom);

  const [sortBy, setSortBy] = useState("");
  //Permissions
  const hasCreatePermission = checkPermissions("create_project");
  const hasEditPermission = checkPermissions("edit_project");
  const hasViewPermission = checkPermissions("view_project", true, toast);

  useEffect(() => {
    setLoading(true);
    getProject({
      page: page,
      length: pageLength,
      search: debouncedInputVal,
      sort_by: sortBy,
      ...(filteredProjectStatus && { is_public: filteredProjectStatus }),
    })
      .then((response) => {
        const projects = response.data.project;
        setSettableData(projects);
        setTotalCount(response.data.pagination.total_count);
        setProjectCount(response.data.pagination.total_count);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
        setLoading(false);
      });
  }, [page, pageLength, debouncedInputVal, sortBy, filteredProjectStatus]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      // If already sorting by this field, toggle to descending
      setSortBy(`-${field}`);
    } else if (sortBy === `-${field}`) {
      // If already sorting by this field descending, clear sort
      setSortBy("");
    } else {
      // Otherwise, sort by this field ascending
      setSortBy(field);
    }
  };

  // Helper function to determine sort direction for a field
  const getSortDirection = (field: string) => {
    if (sortBy === field) return "asc";
    if (sortBy === `-${field}`) return "desc";
    return "none";
  };

  const COLUMN = [
    {
      id: "sr",
      Header: () => <div>Sr. No</div>,
      width: "5%",
      accessor: "sr",
      Cell: ({ row }: { row: any }) => <div>{startIndex + row.index + 1}</div>,
    },
    {
      id: "name",
      Header: ({ column }: { column: any }) => <div>Project name</div>,
      accessor: "name",
      width: 50,
      Cell: ({ value, row }: any) => (
        <Link to={`/projects/${row.original.id}`}>
          <div
            className="underline underline-offset-2 hover:scale-105 hover:pl-1 break-words w-[100px] truncate ... "
            title={value}
          >
            {value}
          </div>
        </Link>
      ),
    },
    {
      id: "location",
      Header: ({ column }: { column: any }) => <div>Location</div>,
      accessor: "location",
      width: 100,
      Cell: ({ row }: any) => {
        const { address_city, address_state } = row.original;

        const location =
          address_city && address_state
            ? `${address_city}, ${
                stateList?.find(
                  (state: any) => state?.state_code === address_state
                )?.name
              }`
            : "-";
        return <div className="w-[200px] break-words">{location}</div>;
      },
    },
    {
      id: "project_status",
      Header: ({ column }: { column: any }) => <div>Project Status</div>,
      accessor: "project_status",
      width: 90,
      Cell: ({ row }: any) => {
        const { is_public } = row.original;
        return (
          <div className="w-[70px]">{is_public ? "Public" : "Private"}</div>
        );
      },
    },
    {
      id: "created_at",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-center items-center gap-2 cursor-pointer"
          onClick={() => handleSort("created_at")}
        >
          <p>Date</p>
          <div className="w-4 h-4 flex items-center justify-center">
            <UpArrow2
              className={`h-4 w-4 transition-transform ${
                getSortDirection("created_at") === "desc"
                  ? "rotate-180"
                  : getSortDirection("created_at") === "none"
                  ? "opacity-50"
                  : ""
              }`}
            />
          </div>
        </div>
      ),
      width: 90,
      accessor: "created_at",
      Cell: ({ value }: any) => (
        <div>
          {formatDate(value)} {/* Render the formatted date */}
        </div>
      ),
    },
    {
      id: "floor_count",
      Header: ({ column }: { column: any }) => (
        <div className="flex justify-center items-center gap-2 ">
          <p>Floors</p>
        </div>
      ),
      width: 50,
      accessor: "floor_count",
    },
    {
      id: "area_count",
      Header: ({ column }: { column: any }) => (
        <div className="flex justify-center items-center gap-2 ">
          <p>Areas</p>
        </div>
      ),
      width: 50,
      accessor: "area_count",
    },
    {
      id: "order_count",
      Header: ({ column }: { column: any }) => (
        <div className="flex justify-center items-center gap-2 ">
          <p>Orders</p>
        </div>
      ),
      width: 50,
      accessor: "order_count",
      Cell: ({ value }: any) => <div>{value} </div>,
    },
    {
      id: "member_count",
      Header: ({ column }: { column: any }) => (
        <div className="flex justify-center items-center gap-2 ">
          <p>Professionals</p>
        </div>
      ),
      width: "4vw",
      accessor: "member_count",
      Cell: ({ value }: any) => <div>{value} </div>,
    },
    ...(hasEditPermission
      ? [
          {
            id: "action",
            width: "12%",
            Header: ({ column }: { column: any }) => (
              <div className="flex justify-center items-center w-full">
                <p className="text-center">Action Item</p>
              </div>
            ),
            accessor: "",
            Cell: ({ row }: any) => (
              <div className="flex gap-x-2 px-2 items-center justify-center">
                {hasEditPermission && (
                  <>
                    <Tooltip title="Edit">
                      <Link to={`/projects/${row.original.id}/edit`}>
                        <div className="bg-pot-black p-1.5 rounded-md my-1">
                          <EditIcon color="white" />
                        </div>
                      </Link>
                    </Tooltip>
                  </>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    const lastIndex = (page - 1) * pageLength;
    setStartIndex(lastIndex);
  }, [page, pageLength, totalCount]);

  const columns = useMemo(() => COLUMN, [settableData]);
  const data = useMemo(() => settableData || [], [settableData]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: page, pageSize: pageLength },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / pageLength),
      useSticky,
    },
    useSortBy,
    usePagination
  );

  const handlePageChange = (page: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (size: any) => {
    setPageLength(size);
  };

  if (!hasViewPermission) {
    navigate("/unauthorized");
  }
  return (
    <div className="h-full">
      <div className="flex flex-row justify-between items-center py-2 w-full px-3 h-[3rem] ">
        <div className="flex gap-4 w-[max-content]">
          <div className=" w-[235px]">
            <InputField
              onChange={(e: string) => {
                setInputVal(e);
                if (page !== 1) {
                  setPage(1);
                }
              }}
              placeholder="Search"
              value={inputVal}
              rightIcon={<SearchIcon />}
            />
          </div>
          {hasCreatePermission && (
            <div className="w-fit">
              <Link to={"/projects/create"}>
                <Button label="Create project" leftIcon={<PlusIcon />} />
              </Link>
            </div>
          )}
        </div>
        <div className="mb-6">
          <SelectField2
            disabled={loading}
            label="Project Status"
            placeholder="Select project status"
            value={filteredProjectStatus}
            onChange={(e) => {
              if (e === filteredProjectStatus) {
                setFilteredProjectStatus("");
              } else {
                setFilteredProjectStatus(e);
              }
              setPage(1);
            }}
            removeOption={() => {
              setFilteredProjectStatus("");
            }}
            options={PROJECT_STATUS?.map((el: any) => ({
              value: el.id,
              label: el.name,
            }))}
            leftIcon={<FilterIcon />}
          />
        </div>
      </div>

      <div className="h-[calc(100%-5.5rem)] w-full relative -z-0">
        <ManagementTableLayout
          tableInstance={tableInstance}
          loading={loading}
        />
      </div>

      {loading ? (
        ""
      ) : (
        <div className="w-full">
          <Pagination
            currentPage={page}
            setCurrentPage={setPage}
            pageSize={pageLength}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}
    </div>
  );
};

export default ProjectTable;
